
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { ArgsTable, Canvas, Meta, Story } from '@storybook/addon-docs';
import blockImageDemo from './demo/image.twig';
import blockCodeDemo from './demo/code.twig';
import blockGroupDemo from './demo/group.twig';
import blockTableDemo from './demo/table.twig';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Vendor/WordPress/Core Blocks" mdxType="Meta" />
    <h1>{`WordPress Core Blocks`}</h1>
    <p>{`The WordPress `}<a parentName="p" {...{
        "href": "https://wordpress.org/gutenberg/"
      }}>{`Gutenberg editor`}</a>{` is a block-based
page builder. The editor comes with a variety of core blocks. Of those, a subset
automatically generate markup with one or more `}<inlineCode parentName="p">{`wp-block-`}</inlineCode>{`-prefixed classes.
These are the blocks documented below.`}</p>
    <p>{`Each block has controls that can modify the generated output. Where relevant, the
additional markup and/or classes that may be generated are also documented.`}</p>
    <h3>{`Editor Controls`}</h3>
    <p>{`Block editor controls vary, but many have Styles and Color settings. The effects
of other controls are described in specific block stories.`}</p>
    <h4>{`Styles`}</h4>
    <p>{`These controls offer 2 or more options for particular stylistic variations, and
the ability to set one as a default for new blocks of the same type. For example,
images can have a circular mask or not. These options add an `}<inlineCode parentName="p">{`is-style-`}</inlineCode>{`-type class
to the block.`}</p>
    <h4>{`Color settings`}</h4>
    <p>{`These controls enable setting of the block's `}<inlineCode parentName="p">{`background-color`}</inlineCode>{` and `}<inlineCode parentName="p">{`color`}</inlineCode>{`.`}</p>
    <p>{`The base class for a block with a main (background) color is `}<inlineCode parentName="p">{`has-background`}</inlineCode>{` and
selecting a text color adds a `}<inlineCode parentName="p">{`has-text-color`}</inlineCode>{` class. If one of a set of predefined
colors is chosen, additional classes are added, such as `}<inlineCode parentName="p">{`has-subtle-background-background-color`}</inlineCode>{`
for `}<inlineCode parentName="p">{`background-color`}</inlineCode>{` or `}<inlineCode parentName="p">{`has-secondary-color`}</inlineCode>{` for text.`}</p>
    <p>{`If user-defined colors are chosen, instead, inline styles will set `}<inlineCode parentName="p">{`color`}</inlineCode>{` and/or
`}<inlineCode parentName="p">{`background-color`}</inlineCode>{`.`}</p>
    <h2>{`Audio`}</h2>
    <p>{`The audio block wraps an `}<inlineCode parentName="p">{`audio`}</inlineCode>{` and optional `}<inlineCode parentName="p">{`figcaption`}</inlineCode>{` element in a layout
container.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Audio" mdxType="Story">{`
    <figure class="wp-block-audio type">
      <audio controls="" src="/media/piano.ogg"></audio>
      <figcaption>My audio clip</figcaption>
    </figure>
  `}</Story>
    </Canvas>
    <h2>{`Button`}</h2>
    <p>{`The button block adds call-to-action-style links to a post. When multiple buttons are added the `}<inlineCode parentName="p">{`wp-block-buttons`}</inlineCode>{` container controls layout.`}</p>
    <p>{`If chosen, a button can have only an outline (no fill color), which adds the `}<inlineCode parentName="p">{`is-style-outline`}</inlineCode>{`
class on the `}<inlineCode parentName="p">{`.wp-block-button`}</inlineCode>{` element.`}</p>
    <p>{`Controls allow each button to have custom text color, background color or gradient,
and border radius.`}</p>
    <Canvas isColumn mdxType="Canvas">
  <Story name="Button" mdxType="Story">
    {`<div class="wp-block-button is-style-fill">
        <a class="wp-block-button__link" href="#">
          Single button
        </a>
      </div>`}
  </Story>
  <Story name="Buttons" mdxType="Story">
    {`<div class="wp-block-buttons">
        <div class="wp-block-button is-style-fill">
          <a class="wp-block-button__link" href="#">
            Button 1 (Default/Fill)
          </a>
        </div>
        <div class="wp-block-button is-style-outline">
          <a class="wp-block-button__link" href="#">
            Button 2 (Outline)
          </a>
        </div>
    </div>`}
  </Story>
    </Canvas>
    <h2>{`Code`}</h2>
    <p>{`The code block adds the class `}<inlineCode parentName="p">{`wp-block-code`}</inlineCode>{` to a `}<inlineCode parentName="p">{`pre`}</inlineCode>{` tag wrapping content inside
`}<inlineCode parentName="p">{`code`}</inlineCode>{` tags. We apply the same CSS as `}<inlineCode parentName="p">{`o-container__fill-pad`}</inlineCode>{` to pull the code block
out so the code itself aligns with the page content. This also applies to fenced
code sections in Markdown blocks.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Code" mdxType="Story">{() => blockCodeDemo()}</Story>
    </Canvas>
    <h2>{`Columns`}</h2>
    <p>{`The columns block creates a set of nested `}<inlineCode parentName="p">{`div`}</inlineCode>{`s. The top wrapper `}<inlineCode parentName="p">{`div`}</inlineCode>{` has a class
of `}<inlineCode parentName="p">{`wp-block-columns`}</inlineCode>{` and each `}<inlineCode parentName="p">{`div`}</inlineCode>{` below that has a class of `}<inlineCode parentName="p">{`wp-block-column`}</inlineCode>{`.
Each column can contain arbitrary content.`}</p>
    <p>{`Controls set up the sizing of columns by adding an inline style with a `}<inlineCode parentName="p">{`flex-basis`}</inlineCode>{`
declaration. Therefore, it's assumed that the `}<inlineCode parentName="p">{`.wp-block-columns`}</inlineCode>{` wrapper must be
set to `}<inlineCode parentName="p">{`display: flex`}</inlineCode>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Columns" mdxType="Story">
    {`<div class="wp-block-columns">
        <div class="wp-block-column" style="flex-basis: 66.66%">
          <h2>Our Designers Code, Our Developers Design</h2>
          <p>
            Our sprint-based process works because we’re full of what our industry
            considers unicorns. Our designers write code. Our developers went to art
            school.
          </p>
          <p>
            We didn’t set out to become a unicorn safe haven, but we’re happy it
            happened. <a href="https://cloudfour.com/is">Our team</a>’s unique
            combination of skills is what enables us to do great work for our clients.
          </p>
        </div>
        <div class="wp-block-column" style="flex-basis: 33.33%">
          <figure class="wp-block-image size-full">
            <img
              src="https://cloudfour.com/wp-content/uploads/2016/07/approach-unicorn.svg"
              alt=""
            />
          </figure>
        </div>
      </div>`}
  </Story>
    </Canvas>
    <h2>{`Cover`}</h2>
    <p>{`Adds an image or video with a text overlay. The cover block is a `}<inlineCode parentName="p">{`div`}</inlineCode>{` with a class
of `}<inlineCode parentName="p">{`wp-block-cover`}</inlineCode>{`. This `}<inlineCode parentName="p">{`div`}</inlineCode>{` has a `}<inlineCode parentName="p">{`style`}</inlineCode>{` attribute by default that sets its
`}<inlineCode parentName="p">{`background-image`}</inlineCode>{` and `}<inlineCode parentName="p">{`-color`}</inlineCode>{`.`}</p>
    <p>{`Controls set classes on the wrapper that adjust the opacity of the `}<inlineCode parentName="p">{`background-color`}</inlineCode>{`.
The base class for this control is `}<inlineCode parentName="p">{`has-background-dim`}</inlineCode>{`, and a percentage is specified
in a modifier class, e.g., `}<inlineCode parentName="p">{`has-background-dim-40`}</inlineCode>{`.`}</p>
    <p>{`Another control results in setting `}<inlineCode parentName="p">{`background-attachment`}</inlineCode>{` to `}<inlineCode parentName="p">{`fixed`}</inlineCode>{`, by adding
the class `}<inlineCode parentName="p">{`has-parallax`}</inlineCode>{`.`}</p>
    <p>{`The text overlay inside the cover block its own `}<inlineCode parentName="p">{`div`}</inlineCode>{` wrapper with a class of
`}<inlineCode parentName="p">{`wp-block_cover__inner-container`}</inlineCode>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Cover" mdxType="Story">
    {`<div
        class="wp-block-cover has-background-dim-40 has-background-dim has-parallax"
        style="background-image:url(/media/Windbuchencom.jpg);background-color:#065174"
      >
        <div class="wp-block-cover__inner-container">
          <p style="font-size:48px" class="has-text-align-center">
            <strong>Snow Patrol</strong>
          </p>
        </div>
      </div>`}
  </Story>
    </Canvas>
    <h3>{`Cover - Video Variant`}</h3>
    <Canvas mdxType="Canvas">
  <Story name="Cover (Video)" mdxType="Story">
    {`<div class="wp-block-cover has-background-dim">
        <div class="vsc-controller" data-vscid="y4nfjhbvo">
        </div>
        <video
          class="wp-block-cover__video-background"
          autoplay
          muted
          data-origwidth="1920"
          data-origheight="1080"
          src="/media/waterfall_edit.mp4"
          style="width: 100%;"
          data-vscid="y4nfjhbvo"
          type="video/mp4">
        </video>
        <div class="wp-block-cover__inner-container">
          <p class="has-text-align-center has-large-font-size">Waterfall</p></p>
        </div>
      </div>`}
  </Story>
    </Canvas>
    <h2>{`Embed`}</h2>
    <p>{`The embed block wraps any embedded content in a `}<inlineCode parentName="p">{`figure`}</inlineCode>{` element with class
`}<inlineCode parentName="p">{`wp-block-embed`}</inlineCode>{` and an inner `}<inlineCode parentName="p">{`div`}</inlineCode>{` with class `}<inlineCode parentName="p">{`wp-block-embed__wrapper`}</inlineCode>{`. Many
specific embed block types build on this core embed block, wrapping a specific embedded
content and adding `}<inlineCode parentName="p">{`is-type-embedType-embed`}</inlineCode>{`, `}<inlineCode parentName="p">{`wp-block-embed-embedType`}</inlineCode>{` and
`}<inlineCode parentName="p">{`is-provider-providerType`}</inlineCode>{` classes to the parent wrapper element.`}</p>
    <p>{`Adding a caption in the editor includes a `}<inlineCode parentName="p">{`figcaption`}</inlineCode>{` element before the closing
`}<inlineCode parentName="p">{`figure`}</inlineCode>{` tag.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Embed" mdxType="Story">
    {`<figure
        class="wp-block-embed-wordpress wp-block-embed is-type-wp-embed is-provider-wp-themes-com"
      >
        <div class="wp-block-embed__wrapper">
          <blockquote
            class="wp-embedded-content"
            data-secret="AbCd1234"
            style="display: none;"
          >
            <a href="https://wp-themes.com/twentytwenty/?p=36"
              >Elements</a
            >
          </blockquote>
          <iframe
            title="Elements — wp-themes.com"
            class="wp-embedded-content"
            sandbox="allow-scripts"
            security="restricted"
            src="https://wp-themes.com/twentytwenty/?p=36/embed/#?secret=AbCd1234"
            data-secret="AbCd1234"
            width="580"
            height="257"
            frameborder="0"
            marginwidth="0"
            marginheight="0"
            scrolling="no"
            data-origwidth="580"
            data-origheight="327"
          ></iframe>
        </div>
      </figure>`}
  </Story>
    </Canvas>
    <h2>{`File`}</h2>
    <p>{`The file block allows downloading or opening media or page attachment content.`}</p>
    <p>{`This block creates a parent `}<inlineCode parentName="p">{`div`}</inlineCode>{` with a `}<inlineCode parentName="p">{`wp-block-file`}</inlineCode>{` class name, which
wraps a single `}<inlineCode parentName="p">{`a`}</inlineCode>{` element. The Gutenberg editor allows file downloads to be
displayed as buttons (`}<inlineCode parentName="p">{`wp-block-file__button`}</inlineCode>{`) or text-only links. Our CSS
styles both configurations as `}<a parentName="p" {...{
        "href": "/docs/components-button--elements"
      }}>{`buttons`}</a>{`.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`c-button--secondary`}</inlineCode>{` and `}<inlineCode parentName="p">{`c-button-tertiary`}</inlineCode>{` class names can be added to the file block's "Advanced → Additional CSS class(es)" input, to output additional button styles.`}</p>
    <p>{`Be aware that the Gutenberg "Show download button" is what adds the `}<inlineCode parentName="p">{`download`}</inlineCode>{` attribute to the `}<inlineCode parentName="p">{`<a>`}</inlineCode>{` element.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="File" mdxType="Story">
    {`<div class="wp-block-file">
        <a href="#" class="wp-block-file__button" download="">
          Primary file download button
        </a>
      </div>
      <div class="wp-block-file">
        <a href="#">
          Primary file download link
        </a>
      </div>
      <div class="wp-block-file c-button--secondary">
        <a href="#" class="wp-block-file__button" download="">
          Secondary file download button
        </a>
      </div>
      <div class="wp-block-file c-button--secondary">
        <a href="">Secondary file download link</a>
      </div>
      <div class="wp-block-file c-button--tertiary">
        <a href="#" class="wp-block-file__button" download="">Tertiary file download button</a>
      </div>
      <div class="wp-block-file c-button--tertiary">
        <a href="#">Tertiary file download link</a>
      </div>`}
  </Story>
    </Canvas>
    <h2>{`Gallery (images)`}</h2>
    <p>{`The gallery block is wrapped in a `}<inlineCode parentName="p">{`figure`}</inlineCode>{` element with the `}<inlineCode parentName="p">{`wp-block-gallery`}</inlineCode>{` class.`}</p>
    <p>{`In the block's settings, the user can choose a number of columns, which adds a `}<inlineCode parentName="p">{`columns-x`}</inlineCode>{` class.`}</p>
    <p>{`The gallery itself is a `}<inlineCode parentName="p">{`ul`}</inlineCode>{` element with the class `}<inlineCode parentName="p">{`blocks-gallery-grid`}</inlineCode>{` and each
item has a class of `}<inlineCode parentName="p">{`blocks-gallery-item`}</inlineCode></p>
    <p>{`A single control sets whether image thumbnails within the gallery are cropped to align
within the grid, adding an `}<inlineCode parentName="p">{`is-cropped`}</inlineCode>{` class to the wrapper element`}</p>
    <p>{`Controls can update items to link to either either their source or their
attachment page. Either selection wraps the media element in an `}<inlineCode parentName="p">{`a`}</inlineCode>{` tag. By default,
items are not wrapped in links.`}</p>
    <p>{`Adding a caption creates a `}<inlineCode parentName="p">{`figcaption`}</inlineCode>{` element before the closing wrapper tag.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Gallery" mdxType="Story">
    {`<figure class="wp-block-gallery columns-2 is-cropped">
        <ul class="blocks-gallery-grid">
          <li class="blocks-gallery-item">
            <figure>
              <a href="/media/Glacial_lakes_Bhutan.jpg">
                <img src="/media/Glacial_lakes_Bhutan.jpg" />
              </a>
            </figure>
          </li>
          <li class="blocks-gallery-item">
            <figure>
              <a href="https://wp-themes.com/twentytwenty/?page_id=2">
                <img src="/media/Sediment_off_the_Yucatan_Peninsula.jpg" />
              </a>
            </figure>
          </li>
          <li class="blocks-gallery-item">
            <figure>
              <img src="/media/Biologia_Centrali-Americana_-_Cantorchilus_semibadius_1902.jpg" />
            </figure>
          </li>
          <li class="blocks-gallery-item">
            <figure>
              <img src="/media/Windbuchencom.jpg" />
            </figure>
          </li>
        </ul>
        <figcaption class="blocks-gallery-caption">A gallery caption</figcaption>
      </figure>`}
  </Story>
    </Canvas>
    <h2>{`Group`}</h2>
    <p>{`A block that groups other (arbitrary) blocks.`}</p>
    <p>{`The group block is wrapped by a `}<inlineCode parentName="p">{`div`}</inlineCode>{` with the class `}<inlineCode parentName="p">{`wp-block-group`}</inlineCode>{`.`}</p>
    <p>{`A secondary wrapper `}<inlineCode parentName="p">{`div`}</inlineCode>{` has the class `}<inlineCode parentName="p">{`wp-block-group__inner-container`}</inlineCode>{`, and provides
a way to set padding on the grouped content within the parent wrapper.`}</p>
    <p>{`The group block has controls for setting both `}<inlineCode parentName="p">{`background-color`}</inlineCode>{` and text `}<inlineCode parentName="p">{`color`}</inlineCode>{`.
See `}<a parentName="p" {...{
        "href": "#color-settings"
      }}>{`color-settings`}</a>{`.`}</p>
    <p>{`By default, grouped blocks that don't set their own `}<inlineCode parentName="p">{`background-color`}</inlineCode>{` and `}<inlineCode parentName="p">{`color`}</inlineCode>{`
will inherit from the group block wrapper, but otherwise they will retain their
own styles.`}</p>
    <p>{`Group blocks with a background color set will automatically gain padding and
negative margins to keep the content visually aligned.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Group" argTypes={{
        background: {
          options: {
            None: '',
            Gray: 'has-gray-lighter-background-color has-background'
          },
          control: {
            type: 'select'
          }
        }
      }} mdxType="Story">
    {args => blockGroupDemo({
          class: args.background
        })}
  </Story>
    </Canvas>
    <h2>{`Image`}</h2>
    <p>{`The image block is wrapped in a `}<inlineCode parentName="p">{`figure`}</inlineCode>{` element with the `}<inlineCode parentName="p">{`wp-block-image`}</inlineCode>{` class.`}</p>
    <p>{`Inputs are available to set `}<inlineCode parentName="p">{`alt`}</inlineCode>{` text, `}<inlineCode parentName="p">{`width`}</inlineCode>{`, and `}<inlineCode parentName="p">{`height`}</inlineCode>{` attributes.`}</p>
    <p>{`Controls set either the `}<inlineCode parentName="p">{`is-style-default`}</inlineCode>{` (no masking) or `}<inlineCode parentName="p">{`is-style-circle-mask`}</inlineCode>{` class on the wrapper element. One of these classes is always present.`}</p>
    <p>{`Additional controls can add wrapper classes based on generated `}<inlineCode parentName="p">{`srcset`}</inlineCode>{` options:
`}<inlineCode parentName="p">{`size-full`}</inlineCode>{`, `}<inlineCode parentName="p">{`size-large`}</inlineCode>{`, `}<inlineCode parentName="p">{`size-medium`}</inlineCode>{`, or `}<inlineCode parentName="p">{`size-thumbnail`}</inlineCode>{`. Further resizing
adds the `}<inlineCode parentName="p">{`is-resized`}</inlineCode>{` class to the wrapper.`}</p>
    <p>{`Adding caption text to the image generates a `}<inlineCode parentName="p">{`figcaption`}</inlineCode>{` element before the
closing wrapper tag.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Image" argTypes={{
        alignment: {
          options: {
            None: '',
            Left: 'alignleft',
            Center: 'aligncenter',
            Right: 'alignright',
            Full: 'alignfull',
            Wide: 'alignwide'
          },
          defaultValue: '',
          control: {
            type: 'select'
          }
        },
        style: {
          options: {
            None: 'is-style-default',
            Outlined: 'is-style-outlined'
          },
          defaultValue: 'is-style-default',
          control: {
            type: 'select'
          }
        }
      }} mdxType="Story">
    {args => blockImageDemo({
          alignment: args.alignment,
          style: args.style
        })}
  </Story>
    </Canvas>
    <h2>{`Media-Text`}</h2>
    <p>{`Combines media and textual content in a wrapper with a class of `}<inlineCode parentName="p">{`wp-block-media-text`}</inlineCode>{`.
The media content is contained in a wrapper with class `}<inlineCode parentName="p">{`wp-block-media-text__media`}</inlineCode>{`
and its text sibling has a class of `}<inlineCode parentName="p">{`wp-block-media-text__content`}</inlineCode>{`.`}</p>
    <p>{`As with the group block, a control can set the parent wrapper's pre-selected or
custom `}<inlineCode parentName="p">{`background-color`}</inlineCode>{`. See `}<a parentName="p" {...{
        "href": "#color-settings"
      }}>{`color settings`}</a>{`.`}</p>
    <p>{`Controls allow the horizontal and vertical alignment and arrangement of the media
and text elements. Choices result in the insertion of additional parent wrapper classes
such as `}<inlineCode parentName="p">{`is-vertically-aligned-center`}</inlineCode>{` and `}<inlineCode parentName="p">{`has-media-on-the-right`}</inlineCode>{`, as well as either
an `}<inlineCode parentName="p">{`alignwide`}</inlineCode>{` (assumes horizontal padding) or `}<inlineCode parentName="p">{`alignfull`}</inlineCode>{` (assumes no horizontal
padding) class.`}</p>
    <p>{`Another control toggles the class `}<inlineCode parentName="p">{`is-stacked-on-mobile`}</inlineCode>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Media-Text" mdxType="Story">
    {`<div class="wp-block-media-text alignwide is-stacked-on-mobile has-media-on-the-right is-vertically-aligned-center has-background has-subtle-background-background-color">
        <figure class="wp-block-media-text__media">
          <img src="/media/Biologia_Centrali-Americana_-_Cantorchilus_semibadius_1902.jpg" alt="" />
        </figure>
        <div class="wp-block-media-text__content">
          <p>The wren<br>Earns his living<br>Noiselessly.</p>
          <p>— Kobayashi Issa (一茶)</p>
        </div>
      </div>`}
  </Story>
    </Canvas>
    <h2>{`Preformatted`}</h2>
    <p>{`The preformatted block allows theming of content within `}<inlineCode parentName="p">{`pre`}</inlineCode>{` tags using the `}<inlineCode parentName="p">{`wp-block-preformatted`}</inlineCode>{`
class.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Preformatted" mdxType="Story">
    {`<pre class="wp-block-preformatted">
       ___________________________
     < I'm an expert in my field. >
       ---------------------------
           \\  ^__^
            \\ (oo)\\_______
              (__)\\       )\\/\\
                  ||----w |
                  ||     ||
      </pre>`}
  </Story>
    </Canvas>
    <h2>{`Pullquote`}</h2>
    <p>{`The pullquote block wraps a `}<inlineCode parentName="p">{`blockquote`}</inlineCode>{` element in a `}<inlineCode parentName="p">{`figure`}</inlineCode>{` element with the class
`}<inlineCode parentName="p">{`wp-block-pullquote`}</inlineCode>{`.`}</p>
    <p><a parentName="p" {...{
        "href": "#color-settings"
      }}>{`Color controls`}</a>{` allow the setting of pre-defined or custom `}<inlineCode parentName="p">{`background-color`}</inlineCode>{` and
text `}<inlineCode parentName="p">{`color`}</inlineCode>{`. A styles control adds one of two wrapper classes, `}<inlineCode parentName="p">{`is-style-default`}</inlineCode>{` or
`}<inlineCode parentName="p">{`is-style-solid-color`}</inlineCode>{`. By default, any user-selected `}<inlineCode parentName="p">{`background-color`}</inlineCode>{` will be
applied only if the solid color background is toggled on.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Pullquote" mdxType="Story">
    {`<figure class="wp-block-pullquote has-background is-style-solid-color">
        <blockquote class="has-text-color has-accent-color">
          <p>One of the hardest things to do in technology is disrupt yourself.</p>
          <cite>Matt Mullenweg</cite>
        </blockquote>
      </figure>`}
  </Story>
    </Canvas>
    <h2>{`Quote`}</h2>
    <p>{`The quote block adds class of `}<inlineCode parentName="p">{`wp-block-quote`}</inlineCode>{` directly to a `}<inlineCode parentName="p">{`blockquote`}</inlineCode>{` element.
It has a control to add an optional `}<inlineCode parentName="p">{`is-style-large`}</inlineCode>{` class.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Quote" mdxType="Story">
    {`<blockquote class="wp-block-quote is-style-large">
        <p>In quoting others, we cite ourselves.</p>
        <cite>Julio Cortázar</cite>
      </blockquote>`}
  </Story>
    </Canvas>
    <h2>{`Separator`}</h2>
    <p>{`The separator block adds the `}<inlineCode parentName="p">{`wp-block-separator`}</inlineCode>{` class to an `}<inlineCode parentName="p">{`hr`}</inlineCode>{` element.`}</p>
    <p>{`Styles controls add `}<inlineCode parentName="p">{`is-style-default`}</inlineCode>{`, `}<inlineCode parentName="p">{`is-style-wide`}</inlineCode>{` or `}<inlineCode parentName="p">{`is-style-dots`}</inlineCode>{` classes.`}</p>
    <p>{`The color controls, if used, can add both `}<inlineCode parentName="p">{`has-text-color`}</inlineCode>{` and `}<inlineCode parentName="p">{`has-background`}</inlineCode>{` classes, as well as modifier classes for predefined color choices. For consistency with `}<a parentName="p" {...{
        "href": "/docs/design-defaults--horizontal-rule"
      }}>{`our default hr`}</a>{`, we only support the text color option.`}</p>
    <Canvas isColumn mdxType="Canvas">
  <Story name="Separator" args={{
        style: 'default'
      }} argTypes={{
        style: {
          options: ['default', 'wide', 'dots'],
          control: {
            type: 'select'
          }
        }
      }} mdxType="Story">
    {args => `<hr class="wp-block-separator is-style-${args.style}">`}
  </Story>
    </Canvas>
    <ArgsTable story="Separator" mdxType="ArgsTable" />
    <h2>{`Spacer`}</h2>
    <p>{`The spacer block is a `}<inlineCode parentName="p">{`div`}</inlineCode>{` with a class of `}<inlineCode parentName="p">{`wp-block-spacer`}</inlineCode>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Spacer" mdxType="Story">
    {`<p>Content before spacer.</p>
        <div
          style="height:50px"
          aria-hidden="true"
          class="wp-block-spacer"
        ></div>
      <p>Content after spacer.</p>`}
  </Story>
    </Canvas>
    <h2>{`Table`}</h2>
    <p>{`The table block renders a table wrapped in a `}<inlineCode parentName="p">{`figure`}</inlineCode>{` element with the class `}<inlineCode parentName="p">{`wp-block-table`}</inlineCode>{`. Controls for this block render optional `}<inlineCode parentName="p">{`thead`}</inlineCode>{` and `}<inlineCode parentName="p">{`tfoot`}</inlineCode>{` sections. A caption input renders a `}<inlineCode parentName="p">{`figcaption`}</inlineCode>{` element within the wrapper and below the `}<inlineCode parentName="p">{`table`}</inlineCode>{` element.`}</p>
    <p>{`Left, right, or center alignment of cell content can be set at the column level, and adds a `}<inlineCode parentName="p">{`has-text-align-*`}</inlineCode>{` class to each affected cell. Another control optionally adds the `}<inlineCode parentName="p">{`has-fixed-layout`}</inlineCode>{` class to the `}<inlineCode parentName="p">{`table`}</inlineCode>{` element: This forces column widths to be identical.`}</p>
    <p>{`WordPress ships with a "stripes" style (using the class `}<inlineCode parentName="p">{`is-style-stripes`}</inlineCode>{`), which we support. We also support `}<inlineCode parentName="p">{`is-style-ruled`}</inlineCode>{` and `}<inlineCode parentName="p">{`is-style-numeric`}</inlineCode>{` classes, which can be added to a WordPress theme using `}<inlineCode parentName="p">{`register_block_style`}</inlineCode>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Table" args={{
        show_header: true,
        show_footer: true,
        show_caption: true
      }} argTypes={{
        block_style: {
          options: ['', 'stripes', 'ruled', 'numeric'],
          type: {
            name: 'enum'
          },
          control: {
            type: 'select'
          }
        },
        column_alignment: {
          options: ['', 'center', 'right'],
          type: {
            name: 'enum'
          },
          control: {
            type: 'select'
          }
        },
        fixed_layout: {
          type: {
            name: 'boolean'
          }
        },
        show_header: {
          type: {
            name: 'boolean'
          }
        },
        show_footer: {
          type: {
            name: 'boolean'
          }
        },
        show_caption: {
          type: {
            name: 'boolean'
          }
        }
      }} mdxType="Story">
    {args => blockTableDemo(args)}
  </Story>
    </Canvas>
    <ArgsTable story="Table" mdxType="ArgsTable" />
    <h2>{`Verse`}</h2>
    <p>{`The verse block adds the `}<inlineCode parentName="p">{`wp-block-verse`}</inlineCode>{` class to a `}<inlineCode parentName="p">{`pre`}</inlineCode>{` element.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Verse" mdxType="Story">
    {`<pre class="wp-block-verse">WHAT was he doing, the great god Pan,<br>    Down in the reeds by the river?<br>Spreading ruin and scattering ban,<br>Splashing and paddling with hoofs of a goat,<br>And breaking the golden lilies afloat<br>    With the dragon-fly on the river.</pre>`}
  </Story>
    </Canvas>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const audio = () => (
          `
    <figure class="wp-block-audio type">
      <audio controls="" src="/media/piano.ogg"></audio>
      <figcaption>My audio clip</figcaption>
    </figure>
  `
        );
audio.storyName = 'Audio';
audio.parameters = { storySource: { source: '`\n    <figure class=\"wp-block-audio type\">\n      <audio controls=\"\" src=\"/media/piano.ogg\"></audio>\n      <figcaption>My audio clip</figcaption>\n    </figure>\n  `' } };

export const button = () => (
          `<div class="wp-block-button is-style-fill">
        <a class="wp-block-button__link" href="#">
          Single button
        </a>
      </div>`
        );
button.storyName = 'Button';
button.parameters = { storySource: { source: '`<div class=\"wp-block-button is-style-fill\">\n        <a class=\"wp-block-button__link\" href=\"#\">\n          Single button\n        </a>\n      </div>`' } };

export const buttons = () => (
          `<div class="wp-block-buttons">
        <div class="wp-block-button is-style-fill">
          <a class="wp-block-button__link" href="#">
            Button 1 (Default/Fill)
          </a>
        </div>
        <div class="wp-block-button is-style-outline">
          <a class="wp-block-button__link" href="#">
            Button 2 (Outline)
          </a>
        </div>
    </div>`
        );
buttons.storyName = 'Buttons';
buttons.parameters = { storySource: { source: '`<div class=\"wp-block-buttons\">\n        <div class=\"wp-block-button is-style-fill\">\n          <a class=\"wp-block-button__link\" href=\"#\">\n            Button 1 (Default/Fill)\n          </a>\n        </div>\n        <div class=\"wp-block-button is-style-outline\">\n          <a class=\"wp-block-button__link\" href=\"#\">\n            Button 2 (Outline)\n          </a>\n        </div>\n    </div>`' } };

export const code = (() => blockCodeDemo());
code.storyName = 'Code';
code.parameters = { storySource: { source: '() => blockCodeDemo()' } };

export const columns = () => (
          `<div class="wp-block-columns">
        <div class="wp-block-column" style="flex-basis: 66.66%">
          <h2>Our Designers Code, Our Developers Design</h2>
          <p>
            Our sprint-based process works because we’re full of what our industry
            considers unicorns. Our designers write code. Our developers went to art
            school.
          </p>
          <p>
            We didn’t set out to become a unicorn safe haven, but we’re happy it
            happened. <a href="https://cloudfour.com/is">Our team</a>’s unique
            combination of skills is what enables us to do great work for our clients.
          </p>
        </div>
        <div class="wp-block-column" style="flex-basis: 33.33%">
          <figure class="wp-block-image size-full">
            <img
              src="https://cloudfour.com/wp-content/uploads/2016/07/approach-unicorn.svg"
              alt=""
            />
          </figure>
        </div>
      </div>`
        );
columns.storyName = 'Columns';
columns.parameters = { storySource: { source: '`<div class=\"wp-block-columns\">\n        <div class=\"wp-block-column\" style=\"flex-basis: 66.66%\">\n          <h2>Our Designers Code, Our Developers Design</h2>\n          <p>\n            Our sprint-based process works because we’re full of what our industry\n            considers unicorns. Our designers write code. Our developers went to art\n            school.\n          </p>\n          <p>\n            We didn’t set out to become a unicorn safe haven, but we’re happy it\n            happened. <a href=\"https://cloudfour.com/is\">Our team</a>’s unique\n            combination of skills is what enables us to do great work for our clients.\n          </p>\n        </div>\n        <div class=\"wp-block-column\" style=\"flex-basis: 33.33%\">\n          <figure class=\"wp-block-image size-full\">\n            <img\n              src=\"https://cloudfour.com/wp-content/uploads/2016/07/approach-unicorn.svg\"\n              alt=\"\"\n            />\n          </figure>\n        </div>\n      </div>`' } };

export const cover = () => (
          `<div
        class="wp-block-cover has-background-dim-40 has-background-dim has-parallax"
        style="background-image:url(/media/Windbuchencom.jpg);background-color:#065174"
      >
        <div class="wp-block-cover__inner-container">
          <p style="font-size:48px" class="has-text-align-center">
            <strong>Snow Patrol</strong>
          </p>
        </div>
      </div>`
        );
cover.storyName = 'Cover';
cover.parameters = { storySource: { source: '`<div\n        class=\"wp-block-cover has-background-dim-40 has-background-dim has-parallax\"\n        style=\"background-image:url(/media/Windbuchencom.jpg);background-color:#065174\"\n      >\n        <div class=\"wp-block-cover__inner-container\">\n          <p style=\"font-size:48px\" class=\"has-text-align-center\">\n            <strong>Snow Patrol</strong>\n          </p>\n        </div>\n      </div>`' } };

export const coverVideo = () => (
          `<div class="wp-block-cover has-background-dim">
        <div class="vsc-controller" data-vscid="y4nfjhbvo">
        </div>
        <video
          class="wp-block-cover__video-background"
          autoplay
          muted
          data-origwidth="1920"
          data-origheight="1080"
          src="/media/waterfall_edit.mp4"
          style="width: 100%;"
          data-vscid="y4nfjhbvo"
          type="video/mp4">
        </video>
        <div class="wp-block-cover__inner-container">
          <p class="has-text-align-center has-large-font-size">Waterfall</p></p>
        </div>
      </div>`
        );
coverVideo.storyName = 'Cover (Video)';
coverVideo.parameters = { storySource: { source: '`<div class=\"wp-block-cover has-background-dim\">\n        <div class=\"vsc-controller\" data-vscid=\"y4nfjhbvo\">\n        </div>\n        <video\n          class=\"wp-block-cover__video-background\"\n          autoplay\n          muted\n          data-origwidth=\"1920\"\n          data-origheight=\"1080\"\n          src=\"/media/waterfall_edit.mp4\"\n          style=\"width: 100%;\"\n          data-vscid=\"y4nfjhbvo\"\n          type=\"video/mp4\">\n        </video>\n        <div class=\"wp-block-cover__inner-container\">\n          <p class=\"has-text-align-center has-large-font-size\">Waterfall</p></p>\n        </div>\n      </div>`' } };

export const embed = () => (
          `<figure
        class="wp-block-embed-wordpress wp-block-embed is-type-wp-embed is-provider-wp-themes-com"
      >
        <div class="wp-block-embed__wrapper">
          <blockquote
            class="wp-embedded-content"
            data-secret="AbCd1234"
            style="display: none;"
          >
            <a href="https://wp-themes.com/twentytwenty/?p=36"
              >Elements</a
            >
          </blockquote>
          <iframe
            title="Elements — wp-themes.com"
            class="wp-embedded-content"
            sandbox="allow-scripts"
            security="restricted"
            src="https://wp-themes.com/twentytwenty/?p=36/embed/#?secret=AbCd1234"
            data-secret="AbCd1234"
            width="580"
            height="257"
            frameborder="0"
            marginwidth="0"
            marginheight="0"
            scrolling="no"
            data-origwidth="580"
            data-origheight="327"
          ></iframe>
        </div>
      </figure>`
        );
embed.storyName = 'Embed';
embed.parameters = { storySource: { source: '`<figure\n        class=\"wp-block-embed-wordpress wp-block-embed is-type-wp-embed is-provider-wp-themes-com\"\n      >\n        <div class=\"wp-block-embed__wrapper\">\n          <blockquote\n            class=\"wp-embedded-content\"\n            data-secret=\"AbCd1234\"\n            style=\"display: none;\"\n          >\n            <a href=\"https://wp-themes.com/twentytwenty/?p=36\"\n              >Elements</a\n            >\n          </blockquote>\n          <iframe\n            title=\"Elements — wp-themes.com\"\n            class=\"wp-embedded-content\"\n            sandbox=\"allow-scripts\"\n            security=\"restricted\"\n            src=\"https://wp-themes.com/twentytwenty/?p=36/embed/#?secret=AbCd1234\"\n            data-secret=\"AbCd1234\"\n            width=\"580\"\n            height=\"257\"\n            frameborder=\"0\"\n            marginwidth=\"0\"\n            marginheight=\"0\"\n            scrolling=\"no\"\n            data-origwidth=\"580\"\n            data-origheight=\"327\"\n          ></iframe>\n        </div>\n      </figure>`' } };

export const file = () => (
          `<div class="wp-block-file">
        <a href="#" class="wp-block-file__button" download="">
          Primary file download button
        </a>
      </div>
      <div class="wp-block-file">
        <a href="#">
          Primary file download link
        </a>
      </div>
      <div class="wp-block-file c-button--secondary">
        <a href="#" class="wp-block-file__button" download="">
          Secondary file download button
        </a>
      </div>
      <div class="wp-block-file c-button--secondary">
        <a href="">Secondary file download link</a>
      </div>
      <div class="wp-block-file c-button--tertiary">
        <a href="#" class="wp-block-file__button" download="">Tertiary file download button</a>
      </div>
      <div class="wp-block-file c-button--tertiary">
        <a href="#">Tertiary file download link</a>
      </div>`
        );
file.storyName = 'File';
file.parameters = { storySource: { source: '`<div class=\"wp-block-file\">\n        <a href=\"#\" class=\"wp-block-file__button\" download=\"\">\n          Primary file download button\n        </a>\n      </div>\n      <div class=\"wp-block-file\">\n        <a href=\"#\">\n          Primary file download link\n        </a>\n      </div>\n      <div class=\"wp-block-file c-button--secondary\">\n        <a href=\"#\" class=\"wp-block-file__button\" download=\"\">\n          Secondary file download button\n        </a>\n      </div>\n      <div class=\"wp-block-file c-button--secondary\">\n        <a href=\"\">Secondary file download link</a>\n      </div>\n      <div class=\"wp-block-file c-button--tertiary\">\n        <a href=\"#\" class=\"wp-block-file__button\" download=\"\">Tertiary file download button</a>\n      </div>\n      <div class=\"wp-block-file c-button--tertiary\">\n        <a href=\"#\">Tertiary file download link</a>\n      </div>`' } };

export const gallery = () => (
          `<figure class="wp-block-gallery columns-2 is-cropped">
        <ul class="blocks-gallery-grid">
          <li class="blocks-gallery-item">
            <figure>
              <a href="/media/Glacial_lakes_Bhutan.jpg">
                <img src="/media/Glacial_lakes_Bhutan.jpg" />
              </a>
            </figure>
          </li>
          <li class="blocks-gallery-item">
            <figure>
              <a href="https://wp-themes.com/twentytwenty/?page_id=2">
                <img src="/media/Sediment_off_the_Yucatan_Peninsula.jpg" />
              </a>
            </figure>
          </li>
          <li class="blocks-gallery-item">
            <figure>
              <img src="/media/Biologia_Centrali-Americana_-_Cantorchilus_semibadius_1902.jpg" />
            </figure>
          </li>
          <li class="blocks-gallery-item">
            <figure>
              <img src="/media/Windbuchencom.jpg" />
            </figure>
          </li>
        </ul>
        <figcaption class="blocks-gallery-caption">A gallery caption</figcaption>
      </figure>`
        );
gallery.storyName = 'Gallery';
gallery.parameters = { storySource: { source: '`<figure class=\"wp-block-gallery columns-2 is-cropped\">\n        <ul class=\"blocks-gallery-grid\">\n          <li class=\"blocks-gallery-item\">\n            <figure>\n              <a href=\"/media/Glacial_lakes_Bhutan.jpg\">\n                <img src=\"/media/Glacial_lakes_Bhutan.jpg\" />\n              </a>\n            </figure>\n          </li>\n          <li class=\"blocks-gallery-item\">\n            <figure>\n              <a href=\"https://wp-themes.com/twentytwenty/?page_id=2\">\n                <img src=\"/media/Sediment_off_the_Yucatan_Peninsula.jpg\" />\n              </a>\n            </figure>\n          </li>\n          <li class=\"blocks-gallery-item\">\n            <figure>\n              <img src=\"/media/Biologia_Centrali-Americana_-_Cantorchilus_semibadius_1902.jpg\" />\n            </figure>\n          </li>\n          <li class=\"blocks-gallery-item\">\n            <figure>\n              <img src=\"/media/Windbuchencom.jpg\" />\n            </figure>\n          </li>\n        </ul>\n        <figcaption class=\"blocks-gallery-caption\">A gallery caption</figcaption>\n      </figure>`' } };

export const group = (args => blockGroupDemo({
  class: args.background
}));
group.storyName = 'Group';
group.argTypes = {
  background: {
    options: {
      None: '',
      Gray: 'has-gray-lighter-background-color has-background'
    },
    control: {
      type: 'select'
    }
  }
};
group.parameters = { storySource: { source: 'args => blockGroupDemo({\n  class: args.background\n})' } };

export const image = (args => blockImageDemo({
  alignment: args.alignment,
  style: args.style
}));
image.storyName = 'Image';
image.argTypes = {
  alignment: {
    options: {
      None: '',
      Left: 'alignleft',
      Center: 'aligncenter',
      Right: 'alignright',
      Full: 'alignfull',
      Wide: 'alignwide'
    },
    defaultValue: '',
    control: {
      type: 'select'
    }
  },
  style: {
    options: {
      None: 'is-style-default',
      Outlined: 'is-style-outlined'
    },
    defaultValue: 'is-style-default',
    control: {
      type: 'select'
    }
  }
};
image.parameters = { storySource: { source: 'args => blockImageDemo({\n  alignment: args.alignment,\n  style: args.style\n})' } };

export const mediaText = () => (
          `<div class="wp-block-media-text alignwide is-stacked-on-mobile has-media-on-the-right is-vertically-aligned-center has-background has-subtle-background-background-color">
        <figure class="wp-block-media-text__media">
          <img src="/media/Biologia_Centrali-Americana_-_Cantorchilus_semibadius_1902.jpg" alt="" />
        </figure>
        <div class="wp-block-media-text__content">
          <p>The wren<br>Earns his living<br>Noiselessly.</p>
          <p>— Kobayashi Issa (一茶)</p>
        </div>
      </div>`
        );
mediaText.storyName = 'Media-Text';
mediaText.parameters = { storySource: { source: '`<div class=\"wp-block-media-text alignwide is-stacked-on-mobile has-media-on-the-right is-vertically-aligned-center has-background has-subtle-background-background-color\">\n        <figure class=\"wp-block-media-text__media\">\n          <img src=\"/media/Biologia_Centrali-Americana_-_Cantorchilus_semibadius_1902.jpg\" alt=\"\" />\n        </figure>\n        <div class=\"wp-block-media-text__content\">\n          <p>The wren<br>Earns his living<br>Noiselessly.</p>\n          <p>— Kobayashi Issa (一茶)</p>\n        </div>\n      </div>`' } };

export const preformatted = () => (
          `<pre class="wp-block-preformatted">
       ___________________________
     < I'm an expert in my field. >
       ---------------------------
           \\  ^__^
            \\ (oo)\\_______
              (__)\\       )\\/\\
                  ||----w |
                  ||     ||
      </pre>`
        );
preformatted.storyName = 'Preformatted';
preformatted.parameters = { storySource: { source: '`<pre class=\"wp-block-preformatted\">\n       ___________________________\n     < I\'m an expert in my field. >\n       ---------------------------\n           \\\\  ^__^\n            \\\\ (oo)\\\\_______\n              (__)\\\\       )\\\\/\\\\\n                  ||----w |\n                  ||     ||\n      </pre>`' } };

export const pullquote = () => (
          `<figure class="wp-block-pullquote has-background is-style-solid-color">
        <blockquote class="has-text-color has-accent-color">
          <p>One of the hardest things to do in technology is disrupt yourself.</p>
          <cite>Matt Mullenweg</cite>
        </blockquote>
      </figure>`
        );
pullquote.storyName = 'Pullquote';
pullquote.parameters = { storySource: { source: '`<figure class=\"wp-block-pullquote has-background is-style-solid-color\">\n        <blockquote class=\"has-text-color has-accent-color\">\n          <p>One of the hardest things to do in technology is disrupt yourself.</p>\n          <cite>Matt Mullenweg</cite>\n        </blockquote>\n      </figure>`' } };

export const quote = () => (
          `<blockquote class="wp-block-quote is-style-large">
        <p>In quoting others, we cite ourselves.</p>
        <cite>Julio Cortázar</cite>
      </blockquote>`
        );
quote.storyName = 'Quote';
quote.parameters = { storySource: { source: '`<blockquote class=\"wp-block-quote is-style-large\">\n        <p>In quoting others, we cite ourselves.</p>\n        <cite>Julio Cortázar</cite>\n      </blockquote>`' } };

export const separator = (args => `<hr class="wp-block-separator is-style-${args.style}">`);
separator.storyName = 'Separator';
separator.argTypes = {
  style: {
    options: ['default', 'wide', 'dots'],
    control: {
      type: 'select'
    }
  }
};
separator.args = {
  style: 'default'
};
separator.parameters = { storySource: { source: 'args => `<hr class=\"wp-block-separator is-style-${args.style}\">`' } };

export const spacer = () => (
          `<p>Content before spacer.</p>
        <div
          style="height:50px"
          aria-hidden="true"
          class="wp-block-spacer"
        ></div>
      <p>Content after spacer.</p>`
        );
spacer.storyName = 'Spacer';
spacer.parameters = { storySource: { source: '`<p>Content before spacer.</p>\n        <div\n          style=\"height:50px\"\n          aria-hidden=\"true\"\n          class=\"wp-block-spacer\"\n        ></div>\n      <p>Content after spacer.</p>`' } };

export const table = (args => blockTableDemo(args));
table.storyName = 'Table';
table.argTypes = {
  block_style: {
    options: ['', 'stripes', 'ruled', 'numeric'],
    type: {
      name: 'enum'
    },
    control: {
      type: 'select'
    }
  },
  column_alignment: {
    options: ['', 'center', 'right'],
    type: {
      name: 'enum'
    },
    control: {
      type: 'select'
    }
  },
  fixed_layout: {
    type: {
      name: 'boolean'
    }
  },
  show_header: {
    type: {
      name: 'boolean'
    }
  },
  show_footer: {
    type: {
      name: 'boolean'
    }
  },
  show_caption: {
    type: {
      name: 'boolean'
    }
  }
};
table.args = {
  show_header: true,
  show_footer: true,
  show_caption: true
};
table.parameters = { storySource: { source: 'args => blockTableDemo(args)' } };

export const verse = () => (
          `<pre class="wp-block-verse">WHAT was he doing, the great god Pan,<br>    Down in the reeds by the river?<br>Spreading ruin and scattering ban,<br>Splashing and paddling with hoofs of a goat,<br>And breaking the golden lilies afloat<br>    With the dragon-fly on the river.</pre>`
        );
verse.storyName = 'Verse';
verse.parameters = { storySource: { source: '`<pre class=\"wp-block-verse\">WHAT was he doing, the great god Pan,<br>    Down in the reeds by the river?<br>Spreading ruin and scattering ban,<br>Splashing and paddling with hoofs of a goat,<br>And breaking the golden lilies afloat<br>    With the dragon-fly on the river.</pre>`' } };

const componentMeta = { title: 'Vendor/WordPress/Core Blocks', includeStories: ["audio","button","buttons","code","columns","cover","coverVideo","embed","file","gallery","group","image","mediaText","preformatted","pullquote","quote","separator","spacer","table","verse"],  };

const mdxStoryNameToKey = {"Audio":"audio","Button":"button","Buttons":"buttons","Code":"code","Columns":"columns","Cover":"cover","Cover (Video)":"coverVideo","Embed":"embed","File":"file","Gallery":"gallery","Group":"group","Image":"image","Media-Text":"mediaText","Preformatted":"preformatted","Pullquote":"pullquote","Quote":"quote","Separator":"separator","Spacer":"spacer","Table":"table","Verse":"verse"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;